import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAimCompetitorThreat, useAimShareOfVoice } from '../lib/aimQueries';
import { mergeAliasedThreatRows } from '../lib/threatMerge';
import { displacementSymbol } from '../lib/displacementSymbol';
import AimEmpty, { AimLoading } from '../primitives/AimEmpty';
import { isPending } from '../lib/queryState';
import WindowLabel from '../primitives/WindowLabel';
import RatingColorBar from '../primitives/RatingColorBar';
import HoverTooltip from '../../shared/HoverTooltip';
import CompetitorDetailPanel from '../panels/CompetitorDetailPanel';
import usePagedRows from '../lib/usePagedRows';
import AimPagination from '../primitives/AimPagination';
import { TABLE_FRAME_FLUSH, TABLE_THEAD, TABLE_ROW, TABLE_ROW_CLICKABLE } from '../../shared/tableStyles';

function emergingTooltipContent(r) {
  const mentions = Number(r.mentions ?? 0);
  const remaining = Math.max(0, 5 - mentions);
  const providers = Number(r.providers ?? 0);
  return (
    <>
      <strong>{r.park_name}</strong> was mentioned{' '}
      <strong className="text-amber-300">{mentions}</strong> time{mentions === 1 ? '' : 's'} in this window
      {providers > 0 && (
        <>
          {' '}on <strong className="text-amber-300">{providers}</strong> of 11 providers
        </>
      )}
      {' '}— below the <strong>5-mention</strong> threshold that graduates a competitor from Emerging to an established threat.
      {remaining > 0 ? (
        <>
          {' '}
          <strong className="text-amber-300">{remaining}</strong> more mention{remaining === 1 ? '' : 's'} will move them into the regularly-tracked pool.
        </>
      ) : null}
      {r.avg_position != null && (
        <>
          {' '}Current average position: <strong className="text-amber-300">{Number(r.avg_position).toFixed(1)}</strong>.
        </>
      )}
    </>
  );
}

// Both competitor tags — "Emerging" and "merged N alias(es)" — render as the same
// pill. Defined once so they cannot drift apart again: the merged-alias tag used
// to be bare amber text with a leading middot while Emerging was a filled pill.
const TAG_CLASS = 'inline-flex items-center text-[10px] uppercase tracking-wide font-semibold '
  + 'px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';

function mergedAliasTooltipContent(r) {
  const aliases = Array.isArray(r._aliases) ? r._aliases : [];
  const n = aliases.length;
  return (
    <>
      AI providers referred to <strong>{r.park_name}</strong> under{' '}
      <strong className="text-amber-300">{n}</strong> other name{n === 1 ? '' : 's'} for
      the same place. Those rows were merged into this one so the threat score
      and mention count reflect the competitor as a whole rather than splitting
      its visibility across name variants.
      <span className="mt-2 block">
        Merged alias{n === 1 ? '' : 'es'}:{' '}
        <strong className="text-amber-300">{aliases.join(', ')}</strong>
      </span>
    </>
  );
}

function brandIntrusionTooltipContent(r) {
  const n = Number(r.brand_intrusions || 0);
  return (
    <>
      <strong>{r.park_name}</strong> surfaced in AI answers to{' '}
      <strong className="text-red-300">your own brand queries</strong>{' '}
      <strong className="text-red-300">{n}</strong> time{n === 1 ? '' : 's'} across the
      tracked runs — when guests asked about your property, the AI brought this
      competitor up instead of (or alongside) you. Brand intrusions weigh heavily
      in the threat score because they compete directly for your branded demand.
    </>
  );
}

function ThreatTable({ rows, windowLabel, onSelect }) {
  const { visibleRows, page, setPage, pageCount, totalItems } = usePagedRows(rows, 25);
  if (rows.length === 0) return <AimEmpty message="No competitor threats detected." />;
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
      {/* Header annotates the scope of this table — mentions are aggregated
          across a window of runs per V1 spec p.25, which is why the numbers
          don't match the single-run Share of Voice section below. */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Competitive Threat</h3>
          <WindowLabel label={windowLabel} />
        </div>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
          Mentions, position, and threat score are aggregated across every run in this window.
          {' '}
          <span className={`${TAG_CLASS} align-middle`}>
            Emerging
          </span>
          {' '}flags competitors with 2–4 mentions across the window — tracked but
          not yet established. Drop in mention count without an emerging flag
          means the competitor has crossed the 5-mention threshold.
          {' '}A{' '}
          <span className={`${TAG_CLASS} align-middle`}>
            merged alias
          </span>
          {' '}tag means the AI referred to one competitor by more than one name
          (e.g. "Dead Horse Ranch State Park" and "…Campground"); those rows are
          combined into one so its mentions and threat score aren't split across
          name variants.
        </p>
      </div>
      <div className={`${TABLE_FRAME_FLUSH} responsive-card-table-wrapper`}>
      <table className="responsive-card-table w-full text-sm text-gray-900 dark:text-gray-100 min-w-[760px]">
        <thead className={TABLE_THEAD}>
          <tr>
            <th className="text-left px-4 py-2.5 whitespace-nowrap">Rank</th>
            <th className="text-left px-4 py-2.5 whitespace-nowrap">Competitor</th>
            <th className="text-right px-4 py-2.5 whitespace-nowrap">Threat</th>
            <th className="text-right px-4 py-2.5 whitespace-nowrap">Mentions</th>
            <th className="text-right px-4 py-2.5 whitespace-nowrap">Avg Position</th>
            <th className="text-right px-4 py-2.5 whitespace-nowrap">Providers</th>
            <th className="text-right px-4 py-2.5 whitespace-nowrap">Displacement</th>
            <th className="text-right px-4 py-2.5 whitespace-nowrap">Intrusions</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((r, i) => {
            const disp = displacementSymbol(r.displacement);
            return (
              <tr
                key={r.park_name}
                role="button"
                tabIndex={0}
                aria-label={`Open details for ${r.park_name}`}
                className={`${TABLE_ROW} ${TABLE_ROW_CLICKABLE}`}
                onClick={() => onSelect?.(r.park_name)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect?.(r.park_name);
                  }
                }}
              >
                <td data-label="Rank" className="px-4 py-3 text-gray-500 tabular-nums">{page * 25 + i + 1}</td>
                {/* On <500px the responsive-card-table CSS turns the td into a
                    flex row with the label on the left and the value on the
                    right. flex-wrap + the basis-full + justify-end class on the
                    badge below sends the "Emerging" pill to its own line,
                    right-floated under the competitor name. */}
                <td data-label="Competitor" className="px-4 py-3 font-medium text-gray-900 dark:text-white max-[499px]:!flex-wrap">
                  <span className="inline-flex items-center gap-2 flex-wrap">
                    <span>{r.park_name}</span>
                    {r.confidence === 'emerging' && (
                      <HoverTooltip
                        triggerClassName={`${TAG_CLASS} cursor-help max-[499px]:hidden`}
                        content={emergingTooltipContent(r)}
                      >
                        Emerging
                      </HoverTooltip>
                    )}
                    {Array.isArray(r._aliases) && r._aliases.length > 0 && (
                      <HoverTooltip
                        triggerClassName={`${TAG_CLASS} cursor-help`}
                        content={mergedAliasTooltipContent(r)}
                      >
                        merged {r._aliases.length} alias{r._aliases.length === 1 ? '' : 'es'}
                      </HoverTooltip>
                    )}
                  </span>
                  {r.confidence === 'emerging' && (
                    <span
                      className="hidden max-[499px]:flex max-[499px]:basis-full max-[499px]:justify-end max-[499px]:mt-1"
                      aria-hidden="true"
                    >
                      <HoverTooltip
                        triggerClassName={`${TAG_CLASS} cursor-help`}
                        content={emergingTooltipContent(r)}
                      >
                        Emerging
                      </HoverTooltip>
                    </span>
                  )}
                </td>
                <td data-label="Threat" className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-24"><RatingColorBar value={Number(r.threat_score ?? 0)} tone="threat" /></div>
                    <span className="tabular-nums">{Number(r.threat_score ?? 0).toFixed(0)}</span>
                  </div>
                </td>
                <td data-label="Mentions" className="px-4 py-3 text-right tabular-nums">{r.mentions}</td>
                <td data-label="Avg Position" className="px-4 py-3 text-right tabular-nums">{r.avg_position != null ? r.avg_position.toFixed(1) : '—'}</td>
                <td data-label="Providers" className="px-4 py-3 text-right tabular-nums">{r.providers} of 11</td>
                <td data-label="Displacement" className={`px-4 py-2 text-right tabular-nums ${disp.color}`}>
                  {disp.arrow ? `${disp.arrow} ${disp.label}` : disp.label}
                </td>
                <td data-label="Intrusions" className={`px-4 py-2 text-right tabular-nums ${Number(r.brand_intrusions || 0) > 0 ? 'text-red-600 dark:text-red-400 font-semibold' : ''}`}>
                  <span className="inline-flex items-center gap-1">
                    {Number(r.brand_intrusions || 0) > 0 && <span>⚠</span>}
                    <span>{r.brand_intrusions ?? 0}</span>
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      <div className="px-4 pb-3">
        <AimPagination page={page} pageCount={pageCount} onPage={setPage} totalItems={totalItems} label="competitor" />
      </div>
    </div>
  );
}

function ShareOfVoice({ rows, loading }) {
  const { visibleRows, page, setPage, pageCount, totalItems } = usePagedRows(rows, 25);
  if (loading) return <AimLoading height={180} />;
  if (!Array.isArray(rows) || rows.length === 0) {
    return <AimEmpty message="No share-of-voice data for this run." />;
  }
  // Percentages stay relative to the full run's mentions, not just this page.
  const total = rows.reduce((s, r) => s + Number(r.mentions ?? 0), 0);
  // Faint vertical gridlines at every 10% across the track so users can
  // read each bar's endpoint precisely — important when shares cluster
  // (e.g. 17.6 / 15.4 / 14.4) and bar lengths alone are hard to tell apart.
  const gridlineStyle = {
    backgroundImage:
      'repeating-linear-gradient(to right, transparent 0, transparent calc(10% - 1px), rgba(148,163,184,0.25) calc(10% - 1px), rgba(148,163,184,0.25) 10%)',
  };
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Share of Voice</h3>
      {/* Scope annotation — this view counts the selected run only, so the
          totals are smaller than the Competitive Threat table above which
          rolls up a window of runs. */}
      <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 mb-3">
        Mentions for the selected run only.
      </p>
      <div className="space-y-2">
        {visibleRows.map((r) => {
          const pct = total > 0 ? (Number(r.mentions) / total) * 100 : 0;
          return (
            <div key={r.park_name} className="text-sm">
              <div className="flex items-center justify-between mb-1 gap-2 max-[344px]:flex-col max-[344px]:items-start max-[344px]:gap-0.5">
                <span className={`font-medium ${r.is_client_park ? 'text-[#32B1FF]' : 'text-gray-900 dark:text-white'}`}>
                  {r.is_client_park ? <span className="mr-1">⭐</span> : null}{r.park_name}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{pct.toFixed(1)}% · {r.mentions} mentions</span>
              </div>
              <div
                className={`w-full h-3 rounded bg-gray-100 dark:bg-gray-800 overflow-hidden ${r.is_client_park ? 'ring-1 ring-[#32B1FF]/50' : ''}`}
                style={gridlineStyle}
              >
                <div className="h-full" style={{ width: `${pct}%`, backgroundColor: r.is_client_park ? '#10B981' : '#6B7280' }} />
              </div>
            </div>
          );
        })}
      </div>
      <div
        aria-hidden="true"
        className="flex justify-between w-full mt-2 text-[10px] text-gray-400 dark:text-gray-500 select-none"
      >
        {[0, 20, 40, 60, 80, 100].map((p) => (
          <span key={p} className="tabular-nums">{p}%</span>
        ))}
      </div>
      <AimPagination page={page} pageCount={pageCount} onPage={setPage} totalItems={totalItems} label="competitor" />
    </div>
  );
}

export default function CompetitorsTab({ clientId, runId }) {
  const threatQuery = useAimCompetitorThreat(clientId, runId);
  const { data: threat } = threatQuery;
  // The hook waits for a run (it would otherwise fetch a default window the drawer doesn't share),
  // so while there is none the tab is still loading, not empty.
  const lThreat = !runId || isPending(threatQuery);
  const windowLabel = threat?.scope?.label;
  const { data: sov, loading: lSov } = useAimShareOfVoice(clientId, runId);
  const [searchParams, setSearchParams] = useSearchParams();
  const selected = searchParams.get('competitor') || null;
  const updateCompetitor = (name) => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    if (name) next.set('competitor', name); else next.delete('competitor');
    return next;
  });

  // Stable tiebreaker chain: threat_score DESC, then mentions DESC, then
  // avg_position ASC, then park_name ASC. The backend orders by threat_score
  // only, so two competitors with equal scores would otherwise flip rank
  // between renders.
  const rows = useMemo(() => {
    const raw = Array.isArray(threat?.rows) ? threat.rows : [];
    // Collapse alias rows (e.g. "Dead Horse Ranch State Park" + "...
    // Campground") before sorting — otherwise the same competitor appears
    // twice in the leaderboard with split mentions.
    const merged = mergeAliasedThreatRows(raw);
    return [...merged].sort((a, b) => {
      const ts = Number(b.threat_score ?? 0) - Number(a.threat_score ?? 0);
      if (ts !== 0) return ts;
      const m = Number(b.mentions ?? 0) - Number(a.mentions ?? 0);
      if (m !== 0) return m;
      const apA = a.avg_position == null ? Number.POSITIVE_INFINITY : Number(a.avg_position);
      const apB = b.avg_position == null ? Number.POSITIVE_INFINITY : Number(b.avg_position);
      if (apA !== apB) return apA - apB;
      return (a.park_name || '').localeCompare(b.park_name || '');
    });
  }, [threat]);
  // Spotlight cards only feature established (>=5 mentions) competitors so a
  // single brand_direct intrusion from an emerging row doesn't displace a
  // long-standing threat. The full table below still shows everyone.
  const established = rows.filter((r) => r.confidence !== 'emerging');
  const top3 = (established.length >= 3 ? established : rows).slice(0, 3);
  const selectedRow = selected ? rows.find((r) => r.park_name === selected) : null;

  return (
    <div className="space-y-5">
      <section>
        <div className="mb-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Top Threats</h3>
            <WindowLabel label={windowLabel} />
          </div>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
            The three competitors with the highest threat score in this window. Threat combines mentions, average rank, providers reached, and brand intrusions.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {lThreat ? (
          <div className="md:col-span-3"><AimLoading height={120} /></div>
        ) : top3.length === 0 ? (
          <div className="md:col-span-3"><AimEmpty message="No competitor threat scores yet — need at least one completed run." /></div>
        ) : (
          top3.map((r, idx) => {
            const disp = displacementSymbol(r.displacement);
            return (
              <div key={r.park_name} className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
                <div className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">#{idx + 1} THREAT</div>
                <div className="mt-1 text-lg font-bold text-gray-900 dark:text-white">{r.park_name}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Threat Score: {r.threat_score}</div>
                <div className="mt-2"><RatingColorBar value={Number(r.threat_score ?? 0)} tone="threat" /></div>
                <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                  {r.mentions} mentions · Pos {r.avg_position != null ? r.avg_position.toFixed(1) : '—'}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">{r.providers} of 11 providers</div>
                {/* Always show displacement; surface brand intrusions alongside it as
                    a separate compact badge so the ranking logic is visible (markup #72). */}
                <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                  {Number(r.brand_intrusions || 0) > 0 && (
                    <>
                      <HoverTooltip
                        triggerClassName="inline-flex items-center gap-1 font-semibold text-red-600 dark:text-red-400 cursor-help"
                        content={brandIntrusionTooltipContent(r)}
                      >
                        ⚠ {r.brand_intrusions} brand intrusion{r.brand_intrusions === 1 ? '' : 's'}
                      </HoverTooltip>
                      <span className="text-gray-400 dark:text-gray-600" aria-hidden="true">·</span>
                    </>
                  )}
                  <span className={disp.color}>
                    {disp.label === 'Stable'
                      ? 'Stable'
                      : `${disp.arrow} Displacement ${disp.label}`}
                  </span>
                </div>
              </div>
            );
          })
        )}
        </div>
      </section>

      {lThreat ? <AimLoading height={220} /> : <ThreatTable rows={rows} windowLabel={windowLabel} onSelect={updateCompetitor} />}
      <ShareOfVoice rows={sov} loading={lSov} />

      {selected && (
        <CompetitorDetailPanel
          clientId={clientId}
          runId={runId}
          competitorName={selected}
          // The drawer's endpoint does not merge name variants, so hand it the
          // aliases this row was merged from and the total the table shows for
          // it — it can then say which mentions its counts leave out and how
          // far apart the two numbers are.
          aliases={selectedRow?._aliases}
          tableMentions={selectedRow?.mentions}
          onClose={() => updateCompetitor(null)}
        />
      )}
    </div>
  );
}
